import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Content,
  Page,
  Header,
  InfoCard,
  MarkdownContent,
} from '@backstage/core-components';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import {
  makeStyles,
  LinearProgress,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  Divider,
  Tooltip,
  Box,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import SendIcon from '@material-ui/icons/Send';
import StopIcon from '@material-ui/icons/Stop';
import DeleteIcon from '@material-ui/icons/Delete';
import ReplayIcon from '@material-ui/icons/Replay';
import { useSessionHistory, ChatMessage } from './useSessionHistory';

// ---- Styles ----

const SIDEBAR_WIDTH = 280;

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: 'flex',
    height: '100%',
    overflow: 'hidden',
  },
  sidebar: {
    width: SIDEBAR_WIDTH,
    minWidth: SIDEBAR_WIDTH,
    borderRight: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.default,
  },
  sidebarHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
  },
  sidebarList: {
    overflowY: 'auto',
    flex: 1,
  },
  sessionItem: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  sessionItemActive: {
    backgroundColor: theme.palette.action.selected,
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
  chatArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
  },
  messageBubble: {
    maxWidth: '80%',
    padding: theme.spacing(1.5, 2),
    borderRadius: 12,
    marginBottom: theme.spacing(1),
    wordBreak: 'break-word',
    lineHeight: 1.5,
    fontSize: '0.875rem',
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      marginBottom: theme.spacing(1),
    },
    '& th, & td': {
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(0.5, 1),
      textAlign: 'left',
    },
    '& pre': {
      backgroundColor: theme.palette.type === 'dark'
        ? theme.palette.grey[900]
        : theme.palette.grey[200],
      padding: theme.spacing(1),
      borderRadius: 4,
      overflowX: 'auto',
    },
    '& code': {
      fontSize: '0.8rem',
    },
    '& p:last-child': {
      marginBottom: 0,
    },
  },
  userMessage: {
    marginLeft: 'auto',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  agentMessage: {
    marginRight: 'auto',
    backgroundColor: theme.palette.type === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.grey[100],
    color: theme.palette.text.primary,
  },
  errorMessage: {
    marginRight: 'auto',
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
  },
  toolChip: {
    display: 'inline-block',
    fontSize: '0.75rem',
    padding: theme.spacing(0.25, 1),
    borderRadius: 8,
    backgroundColor: theme.palette.type === 'dark'
      ? theme.palette.grey[700]
      : theme.palette.grey[300],
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  inputArea: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  inputRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: theme.palette.text.secondary,
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  thinkingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
  },
  '@keyframes pulse': {
    '0%, 80%, 100%': { opacity: 0.3 },
    '40%': { opacity: 1 },
  },
  thinkingDots: {
    display: 'inline-flex',
    gap: 3,
    '& span': {
      width: 6,
      height: 6,
      borderRadius: '50%',
      backgroundColor: theme.palette.text.secondary,
      animation: '$pulse 1.4s infinite ease-in-out both',
    },
    '& span:nth-child(1)': { animationDelay: '0s' },
    '& span:nth-child(2)': { animationDelay: '0.2s' },
    '& span:nth-child(3)': { animationDelay: '0.4s' },
  },
  retryButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(0.25, 1.5),
    fontSize: '0.75rem',
    borderRadius: 12,
    border: `1px solid ${theme.palette.error.main}`,
    backgroundColor: 'transparent',
    color: theme.palette.error.main,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
    },
  },
  activeToolChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '0.75rem',
    padding: theme.spacing(0.25, 1),
    borderRadius: 8,
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(100, 181, 246, 0.15)'
      : 'rgba(33, 150, 243, 0.1)',
    color: theme.palette.type === 'dark'
      ? theme.palette.info.light
      : theme.palette.info.dark,
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
}));

// ---- Helpers ----

/** Replace [doc:ID] citation markers with the backend content URL (intercepted by click handler). */
function resolveDocRefs(text: string, docMap: Map<string, string>, baseUrl: string): string {
  return text.replace(/\[doc:([\w-]+)\]/g, (_match, id: string) => {
    const name = docMap.get(id) ?? 'source';
    return `[📄 ${name}](${baseUrl}/documents/${id}/content)`;
  });
}

/** Strip raw tool call XML blocks that some agents include in streamed text. */
function stripToolCallXml(text: string): string {
  return text
    .replace(/<function_calls>[\s\S]*?<\/function_calls>/g, '')
    .replace(/<function_response>[\s\S]*?<\/function_response>/g, '')
    .trim();
}

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// ---- Route wrapper ----
// Keying on agentName forces React to fully unmount and remount the chat
// component when the user navigates between agents. This guarantees
// isolated state (messages, sessions, streams) per agent — no race
// conditions, no stale streams leaking across agents.

export const ChatWithHistoryPage = () => {
  const { agentName } = useParams<{ agentName: string }>();
  if (!agentName) {
    throw new Error('Agent name is not defined in route params');
  }
  return <ChatWithHistoryPageInner key={agentName} agentName={agentName} />;
};

// ---- Component ----

const ChatWithHistoryPageInner = ({ agentName }: { agentName: string }) => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const agentTitle = agentName
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  const {
    sessions,
    activeSessionId,
    messages,
    isLoading,
    isLoadingSession,
    isInitializing,
    sendMessage,
    switchSession,
    startNewConversation,
    clearSession,
    cancelStreaming,
  } = useSessionHistory({ agentName });

  // Fetch agent description from backend config
  const [agentDescription, setAgentDescription] = useState('');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('aws-genai');
        const resp = await fetchApi.fetch(
          `${baseUrl}/v1/agents/${encodeURIComponent(agentName)}/info`,
        );
        if (resp.ok && !cancelled) {
          const data = await resp.json();
          setAgentDescription(data.description ?? '');
        }
      } catch {
        // not critical
      }
    })();
    return () => { cancelled = true; };
  }, [agentName, discoveryApi, fetchApi]);

  // Load document names and base URL for resolving [doc:ID] citation markers
  const [docNameMap, setDocNameMap] = useState<Map<string, string>>(new Map());
  const [docBaseUrl, setDocBaseUrl] = useState('');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('aws-genai');
        const resp = await fetchApi.fetch(`${baseUrl}/documents`);
        if (resp.ok && !cancelled) {
          const docs: Array<{ id: string; name: string }> = await resp.json();
          setDocNameMap(new Map(docs.map(d => [d.id, d.name])));
          setDocBaseUrl(baseUrl);
        }
      } catch {
        // not critical — citations will fall back to "/genai-builder"
      }
    })();
    return () => { cancelled = true; };
  }, [discoveryApi, fetchApi]);

  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const shouldAutoScroll = useRef(true);

  // wheel and touchmove are only fired by real user interaction,
  // never by programmatic scrollTop assignments — no race condition.
  const handleUserScroll = () => {
    requestAnimationFrame(() => {
      const el = messagesContainerRef.current;
      if (!el) return;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      shouldAutoScroll.current = distanceFromBottom < 50;
    });
  };

  useLayoutEffect(() => {
    if (shouldAutoScroll.current) {
      const el = messagesContainerRef.current;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }
  }, [messages]);

  const handleSend = async () => {
    const text = inputValue.trim();
    if (!text || isLoading) return;
    setInputValue('');
    await sendMessage(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (isInitializing) {
    return (
      <Page themeId="tool">
        <Header title={agentTitle} />
        <Content>
          <LinearProgress />
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool">
      <Header title={agentTitle} />
      <Content noPadding>
        <div className={classes.wrapper}>
          {/* ---- Sidebar ---- */}
          <div className={classes.sidebar}>
            <div className={classes.sidebarHeader}>
              <Typography variant="subtitle2">Conversations</Typography>
              <Tooltip title="New conversation">
                <IconButton size="small" onClick={startNewConversation}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
            <Divider />
            <List className={classes.sidebarList} dense disablePadding>
              {sessions.map(session => (
                <ListItem
                  key={session.sessionId}
                  className={
                    session.sessionId === activeSessionId
                      ? `${classes.sessionItem} ${classes.sessionItemActive}`
                      : classes.sessionItem
                  }
                  onClick={() => switchSession(session.sessionId)}
                  button
                >
                  <ListItemText
                    primary={session.title || 'New conversation'}
                    primaryTypographyProps={{
                      noWrap: true,
                      style: { fontSize: '0.85rem' },
                    }}
                    secondary={formatRelativeDate(session.lastActivity)}
                  />
                </ListItem>
              ))}
              {sessions.length === 0 && (
                <Box p={2}>
                  <Typography variant="body2" color="textSecondary">
                    No conversations yet.
                  </Typography>
                </Box>
              )}
            </List>
          </div>

          {/* ---- Chat area ---- */}
          <div className={classes.chatArea}>
            <div className={classes.messagesContainer} ref={messagesContainerRef} onWheel={handleUserScroll} onTouchMove={handleUserScroll}>
              {isLoadingSession ? (
                <div className={classes.emptyState}>
                  <LinearProgress style={{ width: '60%' }} />
                </div>
              ) : messages.length === 0 ? (
                <div className={classes.emptyState}>
                  <Typography variant="h6" gutterBottom>
                    {agentTitle}
                  </Typography>
                  <Typography variant="body2" style={{ maxWidth: 500 }}>
                    {agentDescription || 'Start a conversation by typing a message below.'}
                  </Typography>
                </div>
              ) : (
                messages.map((msg, idx) => {
                  const isLast = idx === messages.length - 1;
                  const canRetry =
                    isLast &&
                    !isLoading &&
                    msg.type === 'error';
                  return (
                    <MessageBubble
                      key={idx}
                      message={msg}
                      classes={classes}
                      isStreaming={isLoading && isLast}
                      docNameMap={docNameMap}
                      docBaseUrl={docBaseUrl}
                      fetchApi={fetchApi}
                      onRetry={
                        canRetry
                          ? () => {
                              // Find the last user message to resend
                              for (let i = idx - 1; i >= 0; i--) {
                                if (messages[i].type === 'user') {
                                  sendMessage(messages[i].payload);
                                  return;
                                }
                              }
                            }
                          : undefined
                      }
                    />
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* ---- Input area ---- */}
            <div className={classes.inputArea}>
              <InfoCard>
                <div className={classes.inputRow}>
                  <TextField
                    fullWidth
                    multiline
                    maxRows={6}
                    variant="outlined"
                    size="small"
                    placeholder="Type your message..."
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isLoading}
                  />
                  {isLoading ? (
                    <Tooltip title="Stop">
                      <IconButton onClick={cancelStreaming} color="secondary">
                        <StopIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Send">
                      <span>
                        <IconButton
                          onClick={handleSend}
                          color="primary"
                          disabled={!inputValue.trim()}
                        >
                          <SendIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title="End conversation">
                    <span>
                      <IconButton
                        onClick={clearSession}
                        disabled={isLoading || !activeSessionId}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>
              </InfoCard>
            </div>
          </div>
        </div>
      </Content>
    </Page>
  );
};

// ---- Helpers for tool display ----

const TOOL_LABELS: Record<string, string> = {
  'read-techdocs': 'Reading documentation',
  'search-techdocs': 'Searching documentation',
  'search-catalog': 'Searching catalog',
};

function friendlyToolName(name: string): string {
  return TOOL_LABELS[name] ?? name;
}

// ---- Message bubble sub-component ----

function MessageBubble({
  message,
  classes,
  isStreaming,
  docNameMap,
  docBaseUrl,
  fetchApi,
  onRetry,
}: {
  message: ChatMessage;
  classes: ReturnType<typeof useStyles>;
  isStreaming: boolean;
  docNameMap: Map<string, string>;
  docBaseUrl: string;
  fetchApi: { fetch: typeof fetch };
  onRetry?: () => void;
}) {
  const bubbleClass =
    message.type === 'user'
      ? `${classes.messageBubble} ${classes.userMessage}`
      : message.type === 'error'
        ? `${classes.messageBubble} ${classes.errorMessage}`
        : `${classes.messageBubble} ${classes.agentMessage}`;

  const isEmpty = !message.payload.trim();
  const showThinking = isStreaming && isEmpty && message.type === 'agent';

  // Make Bedrock errors friendlier
  const rawPayload =
    message.type === 'error'
      ? message.payload
          .replace(
            /Error:.*Bedrock is unable to process your request.*/i,
            'The AI service is temporarily unavailable. Please try again.',
          )
          .replace(
            /Error:.*too many concurrent requests.*/i,
            'The service is busy. Please wait a moment and try again.',
          )
      : message.payload;

  // For agent messages: strip tool call XML artifacts, then resolve [doc:ID] citation markers
  const displayPayload = message.type === 'agent'
    ? resolveDocRefs(stripToolCallXml(rawPayload), docNameMap, docBaseUrl)
    : rawPayload;

  // Event delegation handler: intercepts clicks on doc content links rendered by MarkdownContent.
  // Uses the real backend URL (not doc://) so react-markdown doesn't strip the href, then
  // prevents the default navigation and fetches with auth headers instead.
  const handleDocLinkClick = async (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href') ?? '';
    if (!href.includes('/documents/') || !href.endsWith('/content')) return;
    e.preventDefault();
    try {
      const resp = await fetchApi.fetch(href);
      if (!resp.ok) return;
      const blob = await resp.blob();
      const contentDisposition = resp.headers.get('Content-Disposition') ?? '';
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch?.[1] ?? 'document';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  return (
    <div>
      {/* Tool calls — shown above the bubble during streaming */}
      {message.tools.length > 0 && (
        <div style={{ marginBottom: 4 }}>
          {message.tools.map((tool, i) => (
            <span
              key={i}
              className={isStreaming ? classes.activeToolChip : classes.toolChip}
            >
              {isStreaming ? friendlyToolName(tool.name) : tool.name}
              {isStreaming && '...'}
            </span>
          ))}
        </div>
      )}

      {/* Thinking indicator — shown when agent has no text yet */}
      {showThinking && message.tools.length === 0 && (
        <div
          className={`${classes.messageBubble} ${classes.agentMessage}`}
          style={{ display: 'inline-block' }}
        >
          <div className={classes.thinkingIndicator}>
            <span>Thinking</span>
            <span className={classes.thinkingDots}>
              <span />
              <span />
              <span />
            </span>
          </div>
        </div>
      )}

      {/* Message content — hidden while empty during streaming */}
      {!isEmpty && (
        <div className={bubbleClass}>
          {message.type === 'user' ? (
            displayPayload
          ) : (
            <div onClick={handleDocLinkClick}>
              <MarkdownContent content={displayPayload} dialect="gfm" />
            </div>
          )}
          {/* Retry button for errors */}
          {message.type === 'error' && onRetry && (
            <button
              className={classes.retryButton}
              onClick={onRetry}
              type="button"
            >
              <ReplayIcon style={{ fontSize: 14 }} />
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  );
}
